import { useMemo } from 'react';
import { format, isToday } from 'date-fns';
import styled from 'styled-components';

import MoreContentRequester from 'components/MoreContentRequester';
import { DayData } from 'services/api/dischargeCalendar';

import useDischargeCalendarDayInfinite from '../hooks/useDischargeCalendarDayInfinite';
import DischargeCalendarPatientCard from '../shared/DischargeCalendarPatientCard';
import { getPayerType } from '../utils/patientUtils';

interface DayColumnProps {
  date: Date;
  dayData?: DayData;
  counts?: { medicare: number; other: number };
  isLoading: boolean;
}

export default function DayColumn({ date, dayData, counts, isLoading }: DayColumnProps) {
  const dayName = format(date, 'EEEE');
  const dayNumber = format(date, 'd');
  const isCurrentDay = isToday(date);

  const infiniteQuery = useDischargeCalendarDayInfinite({
    date,
    initialData: dayData,
    waitForInitialData: true,
  });

  const entries = useMemo(() => {
    if (infiniteQuery.data?.pages) {
      return infiniteQuery.data.pages.flatMap((page) => page.data);
    }
    return dayData?.data ?? [];
  }, [infiniteQuery.data, dayData]);

  const medicareCount = counts?.medicare ?? 0;
  const otherCount = counts?.other ?? 0;

  return (
    <Column data-cy={`day-column-${format(date, 'yyyy-MM-dd')}`}>
      <DayHeader>
        <DayName>{dayName}</DayName>
        <DayNumberWrapper $isToday={isCurrentDay}>
          <DayNumber $isToday={isCurrentDay}>{dayNumber}</DayNumber>
        </DayNumberWrapper>
      </DayHeader>
      <SummaryRow>
        <SummaryItem>
          <SummaryDot $type='medicare' />
          <SummaryText>{isLoading ? '-' : medicareCount} Medicare</SummaryText>
        </SummaryItem>
        <SummaryItem>
          <SummaryDot $type='other' />
          <SummaryText>{isLoading ? '-' : otherCount} Other</SummaryText>
        </SummaryItem>
      </SummaryRow>
      <DayContent>
        {entries.map((entry) => (
          <DischargeCalendarPatientCard key={entry.id} entry={entry} payerType={getPayerType(entry)} />
        ))}
        <MoreContentRequester resource={infiniteQuery} />
      </DayContent>
    </Column>
  );
}

const Column = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 500px;
  min-width: 0;
  border-right: 1px solid ${({ theme }) => theme.colors.black10};

  &:last-child {
    border-right: none;
  }
`;

const DayHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 8px 8px;
  background-color: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.black10};
  gap: 4px;
`;

const DayName = styled.span`
  font-size: 12px;
  font-weight: var(--font-weight-medium);
  color: ${({ theme }) => theme.colors.black};
`;

const DayNumberWrapper = styled.div<{ $isToday: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${({ $isToday, theme }) => ($isToday ? theme.colors.primaryBlue : 'transparent')};
`;

const DayNumber = styled.span<{ $isToday: boolean }>`
  font-size: 12px;
  font-weight: var(--font-weight-medium);
  color: ${({ $isToday, theme }) => ($isToday ? theme.colors.white : theme.colors.black)};
  line-height: 1;
`;

const SummaryRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 8px 12px;
  background-color: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.black10};
`;

const SummaryItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SummaryDot = styled.span<{ $type: 'medicare' | 'other' }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${({ $type, theme }) =>
    $type === 'medicare' ? theme.colors.accentGreen : theme.colors.pictonBlue};
`;

const SummaryText = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.black75};
`;

const DayContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  background-color: ${({ theme }) => theme.colors.white};
  overflow-y: auto;
`;
